import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  School as SchoolIcon,

  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../utils/apiClient';

const ParentSchoolSelection = ({ workflowData, onDataUpdate, onNext, isValid }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Local state
  const [ncesschInput, setNcesschInput] = useState(workflowData.parentNcessch || '');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Search for parent school
  const handleSearch = async () => {
    if (!ncesschInput || ncesschInput.length < 8) {
      setValidationError('Please enter a valid NCESSCH (8+ characters)');
      return;
    }

    setIsSearching(true);
    setValidationError(null);

    try {
      // Use the existing splits test endpoint to validate parent school
      const response = await apiClient.post('/v2/splits/test', {
        identifier: ncesschInput.trim(),
        split_config: {},
        addresses: {}
      });

      if (response) {
        setSearchResults(response);
        
        // Update workflow data with parent school info
        onDataUpdate({
          parentNcessch: ncesschInput.trim(),
          parentSchool: {
            name: response.parent_school?.name || 'Unknown School',
            ncessch: ncesschInput.trim(),
            enrollmentData: response.enrollment_analysis || {},
            teams: response.team_inheritance_analysis || {},
            address: response.parent_school?.address || null
          }
        });

        enqueueSnackbar('Parent school found and validated', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error searching for parent school:', error);
      setValidationError(
        error.message || 
        'Error validating parent school. Please check the NCESSCH and try again.'
      );
      enqueueSnackbar('Error validating parent school', { variant: 'error' });
    } finally {
      setIsSearching(false);
    }
  };

  // Handle enter key in search input
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Format enrollment data for display
  const formatEnrollmentData = (enrollmentData) => {
    if (!enrollmentData || !enrollmentData.by_grade) return [];
    
    return Object.entries(enrollmentData.by_grade).map(([grade, data]) => ({
      grade,
      enrollment: data.total || 0,
      years: data.years || []
    })).sort((a, b) => {
      // Sort grades logically (PK, KG, 01, 02, etc.)
      const gradeOrder = ['PK', 'KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
      return gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade);
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SchoolIcon color="primary" />
        Select Parent School
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Enter the NCESSCH number of the school you want to split. We'll validate the school and show available enrollment data.
      </Typography>

      {/* Search Input */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Parent School NCESSCH"
                value={ncesschInput}
                onChange={(e) => setNcesschInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., 010000500871"
                disabled={isSearching}
                error={!!validationError}
                helperText={validationError || 'Enter the 12-digit NCES school ID'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isSearching || !ncesschInput}
                startIcon={isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                fullWidth
              >
                {isSearching ? 'Searching...' : 'Search School'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Validation Error */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Validation Error</AlertTitle>
          {validationError}
        </Alert>
      )}

      {/* Search Results */}
      {searchResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="h6">
                School Found: {searchResults.parent_school?.name || 'Unknown School'}
              </Typography>
              <Chip label={ncesschInput} color="primary" size="small" />
            </Box>

            {/* School Information */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  School Details
                </Typography>
                <Typography variant="body2">
                  <strong>NCESSCH:</strong> {ncesschInput}
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {searchResults.parent_school?.name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {searchResults.parent_school?.status || 'Active'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Team Assignments
                </Typography>
                {searchResults.team_inheritance_analysis?.teams_to_inherit?.length > 0 ? (
                  searchResults.team_inheritance_analysis.teams_to_inherit.map((team, index) => (
                    <Chip 
                      key={index}
                      label={team.team_name} 
                      size="small" 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No team assignments found
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Enrollment Data */}
            {searchResults.enrollment_analysis && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Available Enrollment Data by Grade
                </Typography>
                
                {formatEnrollmentData(searchResults.enrollment_analysis).length > 0 ? (
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Grade</strong></TableCell>
                          <TableCell><strong>Total Enrollment</strong></TableCell>
                          <TableCell><strong>Data Years</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formatEnrollmentData(searchResults.enrollment_analysis).map((row) => (
                          <TableRow key={row.grade}>
                            <TableCell>{row.grade}</TableCell>
                            <TableCell>{row.enrollment}</TableCell>
                            <TableCell>
                              {row.years.map(year => (
                                <Chip 
                                  key={year} 
                                  label={year} 
                                  size="small" 
                                  sx={{ mr: 0.5 }}
                                />
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>No Enrollment Data</AlertTitle>
                    No enrollment data found for this school. This may limit split processing options.
                  </Alert>
                )}
              </Box>
            )}

            {/* Validation Warnings */}
            {searchResults.validation_results && !searchResults.validation_results.is_valid && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Validation Warnings</AlertTitle>
                {searchResults.validation_results.warnings?.map((warning, index) => (
                  <Typography key={index} variant="body2">
                    • {warning}
                  </Typography>
                ))}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      {!searchResults && (
        <Alert severity="info">
          <AlertTitle>Instructions</AlertTitle>
          <Typography variant="body2">
            1. Enter the NCESSCH number of the school you want to split
          </Typography>
          <Typography variant="body2">
            2. We'll validate the school exists and has enrollment data
          </Typography>
          <Typography variant="body2">
            3. Review the enrollment data to plan your grade assignments
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Note:</strong> You can only split schools that have enrollment data for the grades you want to assign.
          </Typography>
        </Alert>
      )}

      {/* Next Step Button */}
      {isValid && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onNext}
            size="large"
            startIcon={<CheckIcon />}
          >
            Continue to Grade Assignment
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ParentSchoolSelection;
